import type { ResellerInvoice, ResellerLicence } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { date, money, text } from "../../../lib/format.js";
import { timingLabel } from "./renewalTiming.js";

/**
 * What needs the reseller today: what is late, and what is about to renew.
 *
 * THREE STATES, NOT TWO, and the third is the point. A list can have rows, be
 * genuinely empty, or have failed to load — and the BFF keeps those apart on
 * purpose (`[]` versus `null`; see `ResellerOverview`). "Nothing overdue" is
 * worth telling a reseller, and telling them that when the call actually
 * failed is a lie the rest of this app takes some care to avoid: every figure
 * here renders an em dash rather than a zero for the same reason.
 *
 * Every row is a link to the thing itself, because a queue you cannot act
 * from is just a number with extra steps.
 */

function PanelShell({
  title,
  count,
  viewAll,
  children,
}: {
  title: string;
  /** Rendered beside the title when there is a total worth stating. */
  count?: string | null;
  viewAll: { to: string; label: string };
  children: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
            {title}
          </Typography>
          {count && (
            <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
              {count}
            </Typography>
          )}
        </Box>

        <Box
          component={RouterLink}
          to={viewAll.to}
          sx={{
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 500,
            textDecoration: "none",
            color: (t) => t.palette.accent[700],
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {viewAll.label}
        </Box>
      </Box>

      {children}
    </Box>
  );
}

/** The empty and failed states. Two different sentences, deliberately — see
 * the file header. */
function Note({ children, muted = true }: { children: ReactNode; muted?: boolean }): JSX.Element {
  return (
    <Typography
      sx={{
        px: 2.5,
        py: 3,
        fontSize: 13,
        color: muted ? "text.secondary" : "error.main",
      }}
    >
      {children}
    </Typography>
  );
}

/** One tappable row. The whole row is the link — a row where only the title
 * is clickable makes people aim. */
function Row({
  to,
  primary,
  secondary,
  trailing,
  trailingSub,
  trailingAlarming = false,
}: {
  to: string;
  primary: string;
  secondary: string | null;
  trailing: string;
  trailingSub: string | null;
  trailingAlarming?: boolean;
}): JSX.Element {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.5,
        py: 1.5,
        textDecoration: "none",
        color: "inherit",
        borderTop: 1,
        borderColor: "divider",
        "&:first-of-type": { borderTop: 0 },
        transition: (t) => t.transitions.create("background-color", { duration: 120 }),
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": {
          outline: (t) => `2px solid ${t.palette.accent[500]}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {primary}
        </Typography>
        {secondary && (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexShrink: 0, textAlign: "right" }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: trailingAlarming ? "error.main" : "text.primary",
          }}
        >
          {trailing}
        </Typography>
        {trailingSub && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{trailingSub}</Typography>
        )}
      </Box>
    </Box>
  );
}

export function OverduePanel({
  invoices,
  count,
  currency,
}: {
  invoices: ResellerInvoice[] | null;
  count: string | null;
  currency: string | null;
}): JSX.Element {
  return (
    <PanelShell
      title="Overdue"
      count={count}
      // NOT `?payment_status=overdue`: that column holds unpaid / partial /
      // paid / refunded and can never equal "overdue" (overdue is derived
      // from the due date), so the link would open a filtered table showing
      // nothing. Unpaid is the honest superset — the rows above already say
      // which of them are late.
      viewAll={{ to: "/invoices?payment_status=unpaid", label: "All unpaid" }}
    >
      {invoices === null ? (
        <Note muted={false}>Overdue invoices could not be loaded.</Note>
      ) : invoices.length === 0 ? (
        <Note>Nothing overdue — every invoice is inside its terms.</Note>
      ) : (
        invoices.map((invoice) => (
          <Row
            key={invoice.id}
            to={`/invoices/${invoice.id}`}
            primary={text(invoice.number)}
            secondary={invoice.customerName}
            trailing={money(invoice.total, invoice.currency ?? currency)}
            trailingSub={invoice.dueOn ? `due ${date(invoice.dueOn)}` : null}
            trailingAlarming
          />
        ))
      )}
    </PanelShell>
  );
}

/**
 * What runs out next — a LICENCE, on its own date.
 *
 * It used to read the customer's AGREEMENT term, on the reasoning that the two
 * were the same thing. They are not, since a licence became one APPLIANCE: a
 * customer with two boxes holds two licences with two terms, neither of which
 * need match the agreement. So this reads the licences endpoint the licences
 * page reads, and the two cannot disagree about what lapses first.
 *
 * Rows point at the LICENCE rather than the customer — an expiry is not
 * something you read, it is something you renew, and the builder is where a
 * reseller sets the new term and re-issues the file.
 */
export function RenewalsPanel({
  licences,
}: {
  licences: ResellerLicence[] | null;
}): JSX.Element {
  return (
    <PanelShell
      title="Licences expiring"
      count={licences && licences.length > 0 ? "next 60 days" : null}
      viewAll={{ to: "/licences", label: "All licences" }}
    >
      {licences === null ? (
        // "Could not load" is a different fact from "nothing expires", and a
        // front page that confuses them tells a reseller everything is fine.
        <Note muted={false}>Licence expiry dates could not be loaded.</Note>
      ) : licences.length === 0 ? (
        <Note>No licence expires in the next 60 days.</Note>
      ) : (
        licences.map((licence) => (
          <Row
            key={licence.id}
            to={`/licences/${licence.id}`}
            primary={licence.customerName}
            secondary={licence.label || licence.hardwareAddress}
            trailing={licence.expiresOn ? date(licence.expiresOn) : "—"}
            trailingSub={timingLabel(licence.expiresOn)}
          />
        ))
      )}
    </PanelShell>
  );
}
